import CircularProgress from "@mui/material/CircularProgress";
import dayjs from "dayjs";
import "dayjs/locale/nl";
import { useEffect, useState } from "react";

import { getSavedTravelers } from "api/Member";
import AppDialog from "components/dialog";
import useApi from "hooks/useApi";
import { useTranslation } from "i18n";
import {
  maskPassport,
  savedTravelerId,
  savedTravelerInitials,
  savedTravelerName,
} from "utils/savedTravelers";

import "./style.css";

function formatDob(value, locale) {
  if (!value) {
    return "";
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.locale(locale).format("D MMM YYYY") : "";
}

const SavedTravelersModal = ({ open, usedIds = [], onSelect, onClose }) => {
  const { locale, t } = useTranslation();
  const [keyword, setKeyword] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  const { data, loading, request } = useApi(getSavedTravelers, {
    cancelInflight: true,
  });

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedTerm(keyword.trim()), 300);
    return () => clearTimeout(handle);
  }, [keyword]);

  useEffect(() => {
    if (!open) {
      setKeyword("");
      setDebouncedTerm("");
      return;
    }
    request({ search: debouncedTerm });
  }, [open, debouncedTerm]);

  const travelers = Array.isArray(data?.travelers) ? data.travelers : [];

  return (
    <AppDialog open={open} onClose={onClose} padded={false}>
      <div className="bk-saved">
        <header className="bk-saved-head">
          <div>
            <p className="bk-saved-kicker">
              {t("booking.existingTravelers.kicker")}
            </p>
            <h2>{t("booking.existingTravelers.title")}</h2>
          </div>
          <button
            type="button"
            className="bk-saved-close"
            onClick={onClose}
            aria-label={t("common.close")}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M18.3 5.71a1 1 0 00-1.41 0L12 10.59 7.11 5.7A1 1 0 105.7 7.11L10.59 12 5.7 16.89a1 1 0 101.41 1.41L12 13.41l4.89 4.89a1 1 0 001.41-1.41L13.41 12l4.89-4.89a1 1 0 000-1.4z" />
            </svg>
          </button>
        </header>

        <div className="bk-saved-search">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M15.5 14h-.79l-.28-.27A6.47 6.47 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
          </svg>
          <input
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder={t("booking.existingTravelers.searchPlaceholder")}
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
          />
          {keyword ? (
            <button
              type="button"
              className="bk-saved-clear"
              onClick={() => setKeyword("")}
            >
              {t("common.clear")}
            </button>
          ) : null}
        </div>

        {loading && travelers.length === 0 ? (
          <div className="bk-saved-state">
            <CircularProgress size={26} sx={{ color: "var(--primary-color)" }} />
            <span>{t("booking.existingTravelers.loading")}</span>
          </div>
        ) : travelers.length === 0 ? (
          <div className="bk-saved-state">
            <p>
              {keyword
                ? t("booking.existingTravelers.noMatches")
                : t("booking.existingTravelers.empty")}
            </p>
            <span>
              {keyword
                ? t("booking.existingTravelers.noMatchesHint", {
                    query: keyword,
                  })
                : t("booking.existingTravelers.emptyHint")}
            </span>
          </div>
        ) : (
          <div className="bk-saved-list">
            {travelers.map((traveler) => {
              const id = savedTravelerId(traveler);
              const isUsed = usedIds.includes(id);
              const passport = traveler.documents?.[0]?.number;
              const dob = formatDob(traveler.dateOfBirth, locale);
              const meta = [dob, maskPassport(passport)]
                .filter(Boolean)
                .join("  ·  ");

              return (
                <button
                  type="button"
                  key={id}
                  className="bk-saved-row"
                  disabled={isUsed}
                  onClick={() => {
                    onSelect(traveler);
                    onClose();
                  }}
                >
                  <span className="bk-saved-avatar">
                    {savedTravelerInitials(traveler)}
                  </span>
                  <span className="bk-saved-copy">
                    <span className="bk-saved-name">
                      {savedTravelerName(traveler) ||
                        t("booking.existingTravelers.unnamed")}
                    </span>
                    {(meta || traveler.email) && (
                      <span className="bk-saved-meta">
                        {meta || traveler.email}
                      </span>
                    )}
                  </span>
                  <span className="bk-saved-action">
                    {isUsed
                      ? t("booking.existingTravelers.added")
                      : t("booking.existingTravelers.use")}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </AppDialog>
  );
};

export default SavedTravelersModal;
